import React, { useMemo } from "react";
import { Button } from "@/design-system/native";
import styles from "./ExportButtons.module.scss";
// xlsx (~874kB) dinamik import — yalnız export tıklanınca yüklenir,
// ana/paylaşımlı chunk'a girmez (AGENTS §11 / bundle perf).

// Reusable Excel + Print buttons for any table-like data
// Pass rows (array of objects) and columns (header/accessor/formatter) to export.
// You can override default handlers with onExcel/onPrint if needed.
const ExportButtons = ({
  rows = [],
  columns = [],
  fileName = "export",
  sheetName = "Sheet1",
  title,
  onExcel,
  onPrint,
  excludeColumnIds = ["actions"],
  size = "sm",
  className = "",
  disableExcel = false,
  disablePrint = false,
}) => {
  const normalizedColumns = useMemo(() => {
    return columns
      .filter((col) => !excludeColumnIds.includes(col.id))
      .map((col) => {
        const header =
          col.exportLabel && typeof col.exportLabel === "string"
            ? col.exportLabel
            : typeof col.header === "string" || typeof col.header === "number"
            ? String(col.header)
            : col.id ?? col.accessor;

        return {
          id: col.id ?? col.accessor ?? header,
          header,
          accessor: col.exportAccessor ?? col.accessor ?? col.id,
          formatter: col.exportFormatter,
        };
      })
      .filter((col) => col.header && col.accessor);
  }, [columns, excludeColumnIds]);

  const getRows = () => {
    const resolvedRows = typeof rows === "function" ? rows() : rows;
    return Array.isArray(resolvedRows) ? resolvedRows : [];
  };

  const getByPath = (obj, path) => {
    if (!path) return "";
    if (typeof path === "function") return path(obj);
    return String(path)
      .split(".")
      .reduce((acc, key) => (acc && acc[key] != null ? acc[key] : ""), obj);
  };

  const resolveValue = (row, col) => {
    const raw = getByPath(row, col.accessor);
    if (col.formatter) return col.formatter(raw, row) ?? "";
    if (raw === null || raw === undefined) return "";
    if (raw instanceof Date) return raw.toLocaleString();
    if (typeof raw === "object") return JSON.stringify(raw);
    return raw;
  };

  const defaultExcel = async () => {
    const XLSX = await import("xlsx");
    const data = getRows().map((row) => {
      const obj = {};
      normalizedColumns.forEach((col) => {
        obj[col.header] = resolveValue(row, col);
      });
      return obj;
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName || "Sheet1");
    XLSX.writeFile(wb, `${fileName || "export"}.xlsx`);
  };

  const defaultPrint = () => {
    const resolvedRows = getRows();
    const headerHtml = normalizedColumns
      .map((col) => `<th style="padding:8px 10px;border:1px solid #e5e7eb;text-align:left;">${col.header}</th>`)
      .join("");
    const rowsHtml = resolvedRows
      .map((row) =>
        `<tr>${normalizedColumns
          .map(
            (col) =>
              `<td style="padding:8px 10px;border:1px solid #e5e7eb;">${resolveValue(
                row,
                col
              )}</td>`
          )
          .join("")}</tr>`
      )
      .join("");

    const docTitle = title || fileName || "Print";
    const html = `<!doctype html><html><head>
      <title>${docTitle}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 16px; color: #111827; }
        h3 { margin-bottom: 16px; }
        table { border-collapse: collapse; width: 100%; }
        thead { background: #f3f4f6; }
      </style>
      </head><body>
      <h3>${docTitle}</h3>
      <table><thead><tr>${headerHtml}</tr></thead><tbody>${rowsHtml}</tbody></table>
      </body></html>`;

    if (typeof window === "undefined") return;
    const w = window.open("", "_blank");
    w.document.write(html);
    w.document.close();
  };

  const handleExcel = () => {
    if (disableExcel) return;
    if (typeof onExcel === "function") return onExcel();
    return defaultExcel();
  };

  const handlePrint = () => {
    if (disablePrint) return;
    if (typeof onPrint === "function") return onPrint();
    return defaultPrint();
  };

  return (
    <div className={`${styles.wrapper} ${className}`.trim()}>
      {!disableExcel && (
        <Button
          size={size}
          color="white"
          className={`${styles.button} ${styles.excel}`}
          onClick={handleExcel}
        >
          Excel
        </Button>
      )}
      {!disablePrint && (
        <Button
          size={size}
          color="white"
          className={`${styles.button} ${styles.print}`}
          onClick={handlePrint}
        >
          Print
        </Button>
      )}
    </div>
  );
};

export default ExportButtons;
